/**
 * Noise guards for product analytics.
 *
 * Pure logic (no PostHog, no globals) so it can be unit tested:
 *  - dedup window: identical event + key props inside the window are dropped
 *  - session budget: a hard cap on total events, with an always-allowed set
 *    for failures/fallbacks so incidents are never silently swallowed
 *  - once keys: "fire at most once per key" for per-peer state transitions
 */

export const DEDUP_WINDOW_MS = 10_000;
export const SESSION_EVENT_BUDGET = 200;

/** Events that keep flowing even after the session budget is exhausted. */
export const CRITICAL_EVENTS = new Set([
  "peer-connection-failed",
  "peer-connection-recovered",
  "room-reconnect-attempted",
  "sfu-session-failed",
  "video-transport-fallback-started",
  "media-device-fallback-attempted",
  // The diagnostic spine. It carries its own token bucket in `roomTrace.ts`, so
  // counting it against the session budget as well capped nothing extra — it
  // only decided *when* the trail stopped. A room that flaps spends the budget
  // in a few minutes and then goes quiet for the rest of the page, which is the
  // half of the session anybody investigating actually wants to read.
  "room-trace-recorded",
]);

export interface EventGuard {
  allow(event: string, dedupKey?: string, now?: number): boolean;
  allowOnce(key: string): boolean;
  reset(): void;
}

export function createEventGuard(options?: {
  windowMs?: number;
  budget?: number;
  critical?: Set<string>;
}): EventGuard {
  const windowMs = options?.windowMs ?? DEDUP_WINDOW_MS;
  const budget = options?.budget ?? SESSION_EVENT_BUDGET;
  const critical = options?.critical ?? CRITICAL_EVENTS;

  let sent = 0;
  const lastSeen = new Map<string, number>();
  const onceKeys = new Set<string>();

  return {
    allow(event, dedupKey, now = Date.now()) {
      const key = dedupKey ? `${event}|${dedupKey}` : event;
      const previous = lastSeen.get(key);
      if (previous !== undefined && now - previous < windowMs) return false;

      if (sent >= budget && !critical.has(event)) return false;

      lastSeen.set(key, now);
      sent += 1;
      return true;
    },
    allowOnce(key) {
      if (onceKeys.has(key)) return false;
      onceKeys.add(key);
      return true;
    },
    reset() {
      sent = 0;
      lastSeen.clear();
      onceKeys.clear();
    },
  };
}

/** Buckets a chat message length so the text itself never leaves the client. */
export function lengthBucket(length: number): "xs" | "s" | "m" | "l" {
  if (length <= 20) return "xs";
  if (length <= 80) return "s";
  if (length <= 200) return "m";
  return "l";
}

/** Stable, non-reversible short hash so peer ids can be correlated without PII. */
export function hashId(id: string): string {
  let h = 2166136261;
  for (let i = 0; i < id.length; i += 1) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

/** Console entries forwarded per minute, in total. */
export const CONSOLE_BUDGET_PER_MINUTE = 60;
/** How many times one identical message may be forwarded inside a window. */
export const CONSOLE_REPEATS_PER_MESSAGE = 3;

export interface ConsoleBudget {
  allow(message: string, now?: number): boolean;
}

/**
 * Two limits on console forwarding, because one was not enough.
 *
 * The per-minute budget caps the total. The per-message cap is what stops a
 * single line repeating itself into the whole budget — the failure that made
 * this application stop forwarding `log` and `info` altogether was 1357 copies
 * of one React warning, and a total-only budget cannot tell that apart from
 * 1357 different lines worth reading.
 *
 * Keyed on the message rather than on the level: the same line logged at two
 * levels is still the same line saying the same thing.
 */
export function createConsoleBudget(options?: {
  perMinute?: number;
  repeatsPerMessage?: number;
  windowMs?: number;
}): ConsoleBudget {
  const perMinute = options?.perMinute ?? CONSOLE_BUDGET_PER_MINUTE;
  const repeatsPerMessage =
    options?.repeatsPerMessage ?? CONSOLE_REPEATS_PER_MESSAGE;
  const windowMs = options?.windowMs ?? 60_000;

  let windowStart = 0;
  let sentInWindow = 0;
  let repeats = new Map<string, number>();

  return {
    allow(message, now = Date.now()) {
      if (now - windowStart > windowMs) {
        windowStart = now;
        sentInWindow = 0;
        repeats = new Map();
      }
      if (sentInWindow >= perMinute) return false;

      const seen = repeats.get(message) ?? 0;
      if (seen >= repeatsPerMessage) return false;
      repeats.set(message, seen + 1);

      sentInWindow += 1;
      return true;
    },
  };
}
